import { useTable } from '@refinedev/antd';
import { Form, SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { type Key } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCountryContext } from '@/contexts/CountryContext';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';
import { Country } from '@/utils/phoneNumbers';

import useDebouncedValue from './useDebouncedValue';
import { useSelectScrollSearch } from './useSelectScrollSearch';
import { useSelectPropsWithInitial } from './useSelectWithInitialValue';

interface UseLogisticAddressFormProps {
  formProps: FormProps;
  initialValuesModified?: any;
  initialValuesSetted?: boolean;
  setSaveAddress?: (value: boolean) => void;
}

const BRASIL_CEP_LENGTH = 8;
const normalizePostalCode = (value?: string) => `${value ?? ''}`.replace(/\D/g, '');
const initialCepState = {
  loading: false,
  location: undefined as any,
  error: false,
  canEditLocationManually: false,
};

/**
 * Centraliza la gestión de direcciones guardadas, localidades y reseteos dependientes.
 */
export const useLogisticAddressForm = ({
  formProps,
  initialValuesModified,
  initialValuesSetted,
  setSaveAddress,
}: UseLogisticAddressFormProps) => {
  const { packageRepository } = useRepositoriesContext();
  const { isBrasilSelected } = useCountryContext();
  const { form } = formProps;
  const [selectedAddress, setSelectedAddress] = useState<ShippingLabel | undefined>();
  const company = Form.useWatch('company', form);
  const pais = Form.useWatch('receiver_pais', form);
  const provincia = Form.useWatch('receiver_provincia', form);
  const localidad = Form.useWatch('receiver_localidad', form);
  const address = Form.useWatch('receiver_address', form);
  const email = Form.useWatch('receiver_email', form);
  const phone = Form.useWatch('receiver_phone', form);
  const name = Form.useWatch('receiver_name', form);
  const postalCode = Form.useWatch('receiver_postal_code', form);
  const debouncedPostalCode = useDebouncedValue(postalCode, 500);
  const normalizedPostalCode = useMemo(
    () => normalizePostalCode(debouncedPostalCode),
    [debouncedPostalCode],
  );
  const [cepLookupState, setCepLookupState] = useState(initialCepState);
  // Metadata: opciones dependientes compartidas por dirección de envío y dirección de retorno.
  const { selectProps: paisesSelectProps } = useSelectScrollSearch(
    {
      resource: 'paises',
      optionLabel: (item: any) => `${item.nombre}`,
    },
    { searchField: 'nombre' },
  );
  const { selectProps: provinciasSelectProps } = useSelectScrollSearch(
    {
      resource: 'provincias',
      optionLabel: (item: any) => `${item.nombre}`,
      filters: [
        {
          value: pais,
          operator: 'eq' as const,
          field: 'pais',
        },
      ],
      queryOptions: {
        enabled: !!pais && !paisesSelectProps.loading,
      },
    },
    { searchField: 'nombre' },
  );
  const { selectProps: localidadesSelectProps } = useSelectScrollSearch(
    {
      resource: 'localidades',
      optionLabel: (item: any) => `${item.nombre}`,
      filters: [
        {
          value: provincia,
          operator: 'eq' as const,
          field: 'provincia',
        },
      ],
      queryOptions: {
        enabled: !provinciasSelectProps.loading && !!pais && !!provincia,
      },
    },
    {
      saveItem: true,
      searchField: 'nombre',
    },
  );
  const paisesSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: paisesSelectProps,
    resource: 'paises',
    initialValue: initialValuesModified?.receiver_pais,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const provinciasSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: provinciasSelectProps,
    resource: 'provincias',
    initialValue: initialValuesModified?.receiver_provincia,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const localidadesSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: localidadesSelectProps,
    resource: 'localidades',
    initialValue: initialValuesModified?.receiver_localidad,
    labelKey: 'nombre',
    valueKey: 'id',
    getOption: (data: any) => ({
      label: data.nombre,
      value: data.id,
    }),
  });
  // Metadata para diferenciar reseteos manuales de actualizaciones programáticas.
  const previousLocationRef = useRef({
    pais: undefined as unknown,
    provincia: undefined as unknown,
    localidad: undefined as unknown,
    postalCode: undefined as unknown,
  });
  const skipNextLocationResetRef = useRef(false);
  const cepRequestRef = useRef(0);
  const { tableProps } = useTable<ShippingLabel>({
    resource: RESOURCES.SHIPPING_LABELS,
    pagination: { pageSize: 20 },
    meta: {
      empresa: company,
    },
  });
  const countryCode = useMemo(() => Country[pais as keyof typeof Country], [pais]);
  const { dataSource } = tableProps;
  const setProgrammaticFields = useCallback(
    (values: Record<string, unknown>) => {
      if (!form) return;

      // Metadata: evita que los reseteos dependientes borren valores cargados desde CEP o direcciones guardadas.
      skipNextLocationResetRef.current = true;
      form.setFieldsValue(values);
    },
    [form],
  );
  const resetCepState = useCallback(() => {
    setCepLookupState(initialCepState);
  }, []);
  const provinciasOptions = useMemo(() => {
    const options = provinciasSelectPropsWithInitials.options || [];
    const cepProvincia = cepLookupState.location?.provincia;

    if (cepProvincia && !options.some(option => option.value === cepProvincia.id)) {
      return [{ value: cepProvincia.id, label: cepProvincia.nombre }, ...options];
    }

    return options;
  }, [cepLookupState.location?.provincia, provinciasSelectPropsWithInitials.options]);
  const localidadesOptions = useMemo(() => {
    const options = localidadesSelectPropsWithInitials.options || [];
    const cepCiudad = cepLookupState.location?.ciudad;
    const optionsWithCepCiudad =
      cepCiudad && !options.some(option => option.value === cepCiudad.id)
        ? [
            {
              value: cepCiudad.id,
              label: cepCiudad.nombre,
              postal_code: postalCode,
            },
            ...options,
          ]
        : options;

    if (
      selectedAddress &&
      !optionsWithCepCiudad.some(option => option.value === selectedAddress.localidad.id)
    ) {
      return [
        {
          value: selectedAddress.localidad.id,
          label: selectedAddress.localidad.nombre,
          postal_code: selectedAddress.postal_code,
        },
        ...optionsWithCepCiudad,
      ];
    }

    return optionsWithCepCiudad;
  }, [
    cepLookupState.location?.ciudad,
    localidadesSelectPropsWithInitials.options,
    postalCode,
    selectedAddress,
  ]);

  useEffect(() => {
    if (!selectedAddress) return;

    const selectedAddressChanged =
      pais !== selectedAddress.localidad.provincia.pais.id ||
      provincia !== selectedAddress.localidad.provincia.id ||
      localidad !== selectedAddress.localidad.id ||
      address !== selectedAddress.address ||
      email !== selectedAddress.email ||
      phone !== selectedAddress.phone_number ||
      name !== selectedAddress.contact ||
      postalCode !== selectedAddress.postal_code;

    if (selectedAddressChanged) {
      setSelectedAddress(undefined);
    }

    setSaveAddress?.(selectedAddressChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais, provincia, localidad, address, email, phone, name, postalCode]);

  useEffect(() => {
    if (initialValuesSetted && form) {
      previousLocationRef.current = {
        pais: form.getFieldValue('receiver_pais'),
        provincia: form.getFieldValue('receiver_provincia'),
        localidad: form.getFieldValue('receiver_localidad'),
        postalCode: form.getFieldValue('receiver_postal_code'),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesSetted]);

  useEffect(() => {
    if (!form || !initialValuesSetted) {
      return;
    }

    if (skipNextLocationResetRef.current) {
      previousLocationRef.current = { pais, provincia, localidad, postalCode };
      skipNextLocationResetRef.current = false;

      return;
    }

    const previousLocation = previousLocationRef.current;

    if (isBrasilSelected && postalCode !== previousLocation.postalCode) {
      form.setFieldsValue({
        receiver_provincia: undefined,
        receiver_localidad: undefined,
        receiver_address: undefined,
      });
      resetCepState();
      previousLocationRef.current = {
        pais,
        provincia: undefined,
        localidad: undefined,
        postalCode,
      };

      return;
    }

    // Si cambia el país, provincia/localidad/código postal dejan de ser confiables.
    if (pais !== previousLocation.pais) {
      form.setFieldsValue({
        receiver_provincia: undefined,
        receiver_localidad: undefined,
        receiver_postal_code: undefined,
      });
      previousLocationRef.current = {
        pais,
        provincia: undefined,
        localidad: undefined,
        postalCode: undefined,
      };

      return;
    }

    // Si cambia la provincia, localidad/código postal deben elegirse nuevamente.
    if (provincia !== previousLocation.provincia) {
      form.setFieldsValue({
        receiver_localidad: undefined,
        ...(!isBrasilSelected ? { receiver_postal_code: undefined } : {}),
      });
      previousLocationRef.current = {
        pais,
        provincia,
        localidad: undefined,
        postalCode: isBrasilSelected ? postalCode : undefined,
      };

      return;
    }

    if (!isBrasilSelected && localidad !== previousLocation.localidad) {
      if (!localidad && !isBrasilSelected) {
        form.setFieldsValue({
          receiver_postal_code: undefined,
        });
      }

      previousLocationRef.current = { pais, provincia, localidad, postalCode };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais, provincia, localidad, postalCode, initialValuesSetted, isBrasilSelected]);

  useEffect(() => {
    if (!dataSource || dataSource.length === 0) {
      setSaveAddress?.(true);
    }
  }, [dataSource, form, selectedAddress, setSaveAddress]);

  useEffect(() => {
    if (!isBrasilSelected || normalizedPostalCode.length < BRASIL_CEP_LENGTH) {
      cepRequestRef.current += 1;
      resetCepState();

      return;
    }

    const requestId = cepRequestRef.current + 1;

    cepRequestRef.current = requestId;
    setCepLookupState({ ...initialCepState, loading: true });

    void packageRepository
      .getLocationFromCEP(normalizedPostalCode)
      .then(location => {
        if (cepRequestRef.current !== requestId) return;

        setCepLookupState({ ...initialCepState, location });
        setProgrammaticFields({
          receiver_provincia: location?.provincia?.id,
          receiver_localidad: location?.ciudad?.id,
          receiver_address: location?.direccion,
        });
      })
      .catch(() => {
        if (cepRequestRef.current !== requestId) return;

        setCepLookupState({
          ...initialCepState,
          error: true,
        });
      });
  }, [
    isBrasilSelected,
    normalizedPostalCode,
    packageRepository,
    resetCepState,
    setProgrammaticFields,
  ]);

  const handleSavedAddressChange = useCallback(
    (_selectedKeys: Key[], selectedRows: ShippingLabel[]) => {
      const selectedRow = selectedRows[0];

      if (!selectedRow) {
        setSelectedAddress(undefined);
        setSaveAddress?.(true);

        return;
      }

      if (form) {
        setProgrammaticFields({
          receiver_address: selectedRow.address,
          receiver_email: selectedRow.email,
          receiver_phone: selectedRow.phone_number,
          receiver_name: selectedRow.contact,
          receiver_pais: selectedRow.localidad.provincia.pais.id,
          receiver_provincia: selectedRow.localidad.provincia.id,
          receiver_localidad: selectedRow.localidad.id,
          receiver_postal_code: selectedRow.postal_code,
        });
      }

      setSelectedAddress(selectedRow);
      setSaveAddress?.(false);
    },
    [form, setProgrammaticFields, setSaveAddress],
  );
  const handleLocalidadChange = useCallback(
    (value: SelectProps['value'], option: any) => {
      localidadesSelectPropsWithInitials.onChange?.(value, option);

      if (form && initialValuesSetted && option) {
        form.setFieldsValue({ receiver_postal_code: option.item?.postal_code });
      }
    },
    [form, initialValuesSetted, localidadesSelectPropsWithInitials],
  );
  const enableBrazilManualLocation = useCallback(() => {
    setCepLookupState(current => ({ ...current, canEditLocationManually: true }));
  }, []);

  return {
    company,
    pais,
    provincia,
    selectedAddress,
    tableProps,
    dataSource,
    paisesSelectProps: paisesSelectPropsWithInitials,
    provinciasSelectProps: { ...provinciasSelectPropsWithInitials, options: provinciasOptions },
    localidadesSelectProps: localidadesSelectPropsWithInitials,
    countryCode,
    isBrasilSelected,
    localidadesOptions,
    handleSavedAddressChange,
    handleLocalidadChange,
    cepLookupState,
    enableBrazilManualLocation,
  };
};
